import React, { useEffect, useState, useRef, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Col, Form, Row, Spinner } from "reactstrap";
import moment from "moment/moment";
import { EContentService } from "../../../globals/services/enhancedContent.service";
import { SessionsService } from "../../../globals/services/sessions.service";
import { EventsService } from "../../../globals/services/events.service";
import { useSelector } from "react-redux";
import { TABS, STATUS, PERMISSIONS } from "../constants/index";
import EditHeader from "../components/EditHeader";
import ContentItemsSection from "./components/ContentItemsSection";
import RelatedSeminarsSection from "./components/RelatedSeminarsSection";
import RelatedTemplatesSection from "./components/RelatedTemplatesSection";
import styles from "./Folder.module.scss";
import FormField from "../components/FormField";
import SelectField from "../components/SelectField";

export const Folder = () => {
    const history = useHistory();
    const location = useLocation();
    const { folderItem } = location.state || {};
    const { me } = useSelector((state) => state.auth);
    const eContentService = EContentService();
    const sessionService = SessionsService();
    const eventService = EventsService();

    const initialState = {
        _id: folderItem ? folderItem._id : null,
        title: folderItem ? folderItem.title : '',
        subtitle: folderItem ? folderItem.subtitle : null,
        status: folderItem ? folderItem.status : null,
        permissions: folderItem ? folderItem.permissions : [],
        customTabs: folderItem ? folderItem.customTabs : [],
        templates: folderItem ? folderItem.templates : [],
        seminars: folderItem ? folderItem.seminars : [],
        sessions: folderItem ? folderItem.sessions : [],
        contentItems: folderItem ? folderItem.contentItems : [],
        contentTypes: folderItem ? folderItem.contentTypes : [],
        description: folderItem ? folderItem.description : null,
        createdBy: folderItem ? folderItem.createdBy : me ? me._id : null,
        created: folderItem ? folderItem.created : moment().format('YYYY-MM-DD'),
        modifiedBy: me ? me._id : null,
        modified: moment().format('YYYY-MM-DD'),
    }

    const [value, setValue] = useState('');
    const [state, setState] = useState(initialState);
    const [sessionsList, setSessionsList] = useState([]);
    const [filteredTemplateList, setFilteredTemplateList] = useState([]);
    const [contentItemList, setContentItemList] = useState([]);
    const [templateList, setTemplateList] = useState([]);
    const [seminarList, setSeminarList] = useState([]);
    const [hasChanges, setHasChanges] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isMounted, setIsMounted] = useState(true);
    const [selectedSeminars, setSelectedSeminars] = useState(state.seminars);
    const [selectedTemplates, setSelectedTemplates] = useState(state.templates);
    const timeoutRef = useRef(null);

    useEffect(() => {
        return () => {
            setIsMounted(false);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (me) {
            eContentService.getActiveTemplateList().then((res) => {
                setTemplateList(res.data);
            });

            eventService.getEventItems().then((res) => {
                const dateThreeMonthsAgo = moment().subtract(12, 'months').format('YYYY-MM-DD');
                setSeminarList(res.filter(
                    (item) => item.event.dtEndDate && moment(item.event.dtEndDate).format('YYYY-MM-DD') > dateThreeMonthsAgo
                ));
            });

            eContentService.getDivisionContentItems().then((res) => {
                setContentItemList(res);
            });
        }
    }, [me]);

    useEffect(() => {
        setSelectedSeminars(state.seminars);

        if (state.seminars && state.seminars.length > 0) {
            const listEventsIds = state.seminars.map((item) => item.seminarId).filter(id => id);
            if (listEventsIds.length > 0) {
                sessionService.getSessionItemsByEventId(listEventsIds).then((res) => {
                    if (res && Array.isArray(res)) {
                        const allSessions = [];
                        res.forEach((eventItem) => {
                            if (eventItem.sessions && Array.isArray(eventItem.sessions)) {
                                eventItem.sessions.forEach((session) => {
                                    if (session && session._id) {
                                        const sessionWithEventId = {
                                            ...session,
                                            eventId: eventItem._id || eventItem.eventId
                                        };
                                        allSessions.push(sessionWithEventId);
                                    }
                                });
                            }
                        });
                        setSessionsList(allSessions);
                    }
                }).catch((error) => {
                    console.error('Error fetching sessions:', error);
                    setSessionsList([]);
                });
            } else {
                setSessionsList([]);
            }
        } else {
            setSessionsList([]);
        }
    }, [state.seminars]);

    useEffect(() => {
        setSelectedTemplates(state.templates);
    }, [state.templates]);

    useEffect(() => {
        if (templateList) {
            const filtered = templateList.filter((item) => {
                return !state.templates.includes(item._id);
            });
            setFilteredTemplateList(filtered);
        }
    }, [templateList, state.templates]);

    const navigateToFoldersList = () => {
        if (isMounted) {
            history.push('/curricula', { curriculaActiveTab: TABS.FOLDERS });
        }
    };

    const handleForm = (prop) => {
        setState({
            ...state,
            ...prop
        });
        setHasChanges(true);
    }

    const handleSaveFolder = async () => {
        try {
            setIsLoading(true);
            const saveOperation = state._id ? eContentService.updateFolder : eContentService.createFolder;
            await saveOperation(state);
            if (isMounted) {
                navigateToFoldersList();
            }
        } catch (error) {
            console.error('Error saving folder:', error);
        } finally {
            setIsLoading(false);
        }
    }

    const handleDeleteFolder = async () => {
        try {
            setIsLoading(true);
            await eContentService.deleteFolder(state._id);
            if (isMounted) {
                navigateToFoldersList();
            }
        } catch (error) {
            console.error('Error deleting folder:', error);
        } finally {
            setIsLoading(false);
        }
    }

    const handleSession = (name, selectedSessionIds, currentSeminarId) => {
        let existingSessions = state.sessions ? state.sessions.filter(s => s && s.seminarId !== currentSeminarId) : [];

        let newSessions = [];
        selectedSessionIds.forEach(sessionId => {
            const sessionData = sessionsList.find((item) => item._id === sessionId);
            if (sessionData) {
                newSessions.push({
                    sessionId: sessionData._id,
                    sessionTitle: sessionData.sessionTitle,
                    seminarId: sessionData.eventId,
                });
            }
        });

        const allSessions = [...existingSessions, ...newSessions];
        handleForm({ sessions: allSessions });
    }

    const handleTemplateTab = (name, selectedTabIds, currentTemplateId) => {
        let customTabs = state.customTabs ? [...state.customTabs] : [];

        // Update tab selection status for the current template
        customTabs.forEach(tab => {
            if (tab.templateId === currentTemplateId) {
                tab.tabSelected = selectedTabIds.includes(tab.tabTitle);
            }
        });

        handleForm({ customTabs: customTabs });
    }

    const handleSyncTemplateTabs = useCallback((templateId, templateTabItems) => {
        let customTabs = state.customTabs ? [...state.customTabs] : [];
        
        // Store existing selected state for this template
        const existingSelectedTabs = customTabs
            .filter(tab => tab.templateId === templateId)
            .reduce((acc, tab) => {
                acc[tab.tabTitle] = tab.tabSelected;
                return acc;
            }, {});
        
        // Remove existing tabs for this template
        customTabs = customTabs.filter(tab => tab.templateId !== templateId);
        
        // Add tabs from the template
        if (templateTabItems && templateTabItems.length > 0) {
            const selectedTemplate = templateList.find(t => t._id === templateId);
            templateTabItems.forEach((tab) => {
                const tabData = {
                    tabTitle: tab.title,
                    tabOrder: tab.order,
                    templateTitle: selectedTemplate ? selectedTemplate.title : '',
                    templateId: templateId,
                    tabSelected: existingSelectedTabs[tab.title] || false, // Preserve existing selection
                }
                customTabs.push(tabData);
            });
        }
        
        handleForm({ customTabs: customTabs });
    }, [state.customTabs, templateList]);



    const handleSeminar = (value, index = null) => {
        let seminars = state.seminars ? [...state.seminars] : [];

        if (seminarList) {
            const selectedSeminar = seminarList.find(seminar => seminar.event.eventTitle === value);

            if (selectedSeminar) {
                if (index !== null && seminars[index]) {
                    // Update existing seminar
                    seminars[index] = {
                        seminarId: selectedSeminar.event._id,
                        SeminarTitle: selectedSeminar.event.eventTitle,
                    };
                } else {
                    // Add new seminar
                    seminars.push({
                        seminarId: selectedSeminar.event._id,
                        SeminarTitle: selectedSeminar.event.eventTitle,
                    });
                }
            }
        }

        handleForm({ seminars: seminars });
    }

    const handleRemoveSeminar = (e, id) => {
        let seminars = state.seminars ? state.seminars : [];

        if (e && e.preventDefault) {
            // Handle old functionality (removing by id)
            e.preventDefault();
            let index = seminars.indexOf(id);
            if (index > -1) {
                seminars.splice(index, 1);
            }
            setValue(e.target.value);
        } else {
            // Handle new functionality (clearing by index)
            if (typeof id === 'number' && id >= 0 && id < seminars.length) {
                seminars.splice(id, 1);
            }
        }

        handleForm({ seminars: seminars });
    }

    const handleAddSeminar = (e) => {
        e.preventDefault();
        let seminars = state.seminars ? [...state.seminars] : [];
        const newSeminar = {
            seminarId: null,
            SeminarTitle: "",
        };
        seminars.push(newSeminar);
        handleForm({ seminars: seminars });
    };

    const handleTemplate = (value, index = null) => {
        let templates = state.templates ? state.templates : [];
        let customTabs = state.customTabs ? state.customTabs : [];

        if (templateList) {
            const selectedTemplate = templateList.find(template => template._id === value);

            if (selectedTemplate) {
                if (index !== null && index < templates.length) {
                    // Update existing template - remove old tabs first
                    const oldTemplateId = templates[index];
                    customTabs = customTabs.filter(tab => tab.templateId !== oldTemplateId);
                    templates[index] = selectedTemplate._id;
                } else {
                    // Add new template
                    templates.push(selectedTemplate._id);
                }

                // Add tabs from the selected template
                if (selectedTemplate.tabItems) {
                    selectedTemplate.tabItems.forEach((tab) => {
                        const tabData = {
                            tabTitle: tab.title,
                            tabOrder: tab.order,
                            templateTitle: selectedTemplate.title,
                            templateId: selectedTemplate._id,
                            tabSelected: false,
                        }
                        customTabs.push(tabData);
                    });
                }
            }
        }

        handleForm({ customTabs: customTabs, templates: templates });
    }

    const handleAddTemplate = (e) => {
        e.preventDefault();
        let templates = state.templates ? [...state.templates] : [];
        const newTemplate = null;
        templates.push(newTemplate);
        handleForm({ templates: templates });
    };

    const getFilteredOptionsForTemplate = (currentTemplateId) => {
        if (!templateList) return [];

        return templateList.filter((template) => {
            // Always include the current template being edited
            if (currentTemplateId && template._id === currentTemplateId) {
                return true;
            }

            // If no templates are selected yet, show all options
            if (!selectedTemplates || selectedTemplates.length === 0) {
                return true;
            }

            // Filter out templates that are already selected in other sections
            return !selectedTemplates.some((selectedTemplate) => selectedTemplate === template._id);
        });
    };

    const handleRemoveTemplateByIndex = (e, index) => {
        e.preventDefault();
        let templates = state.templates ? state.templates : [];
        if (index >= 0 && index < templates.length) {
            const templateId = templates[index];
            templates.splice(index, 1);
            handleForm({ templates: templates });

            if (state._id && templateId) {
                eContentService.removeTemplateFromFolder(templateId, state._id).then((res) => {
                    return res;
                });
            }

            let customTabs = state.customTabs ? state.customTabs : [];
            const filteredTabs = customTabs.filter(
                (item) => item.templateId !== templateId
            );
            handleForm({ customTabs: filteredTabs });
        }
    }

    const getFilteredOptionsForSection = (currentSeminarId) => {
        if (!seminarList) return [];

        return seminarList.filter((seminar) => {
            if (currentSeminarId && seminar.event._id === currentSeminarId) {
                return true;
            }

            if (!selectedSeminars || selectedSeminars.length === 0) {
                return true;
            }

            return !selectedSeminars.some((selectedSeminar) => selectedSeminar.seminarId === seminar.event._id);
        });
    };

    return (
        <div className="content">
            {isLoading && (
                <div className={styles.loaderOverlay}>
                    <Spinner color="primary" />
                </div>
            )}
            <div className="page-content">
                <div className="container-fluid">
                    <Form>
                        <div className={styles.section}>
                            <EditHeader
                                title="Folder"
                                subTitle="contents"
                                createNew={!state._id}
                                hasChanges={hasChanges}
                                handleSave={handleSaveFolder}
                                handleDelete={handleDeleteFolder}
                                handleGoBack={navigateToFoldersList}
                            />

                            <div className="col-12 col-md-10">
                                <FormField
                                    type="text"
                                    title="Folder Name"
                                    name="title"
                                    value={state.title ? state.title : ""}
                                    onChange={(e) => handleForm({ title: e.target.value })}
                                />
                                <FormField
                                    type="textarea"
                                    title="Folder Description"
                                    name="description"
                                    value={state.description ? state.description : ""}
                                    onChange={(e) => handleForm({ description: e.target.value })}
                                />
                            </div>

                            <Row className="gap-3">
                                <Col className="col-12 col-md-4 mt-4">
                                    <SelectField
                                        label="Status"
                                        name="status"
                                        options={Object.values(STATUS).map((status) => ({ label: status, value: status }))}
                                        value={state.status}
                                        onChange={(name, value) => handleForm({ [name]: value })}
                                        minHeight={55}
                                    />
                                </Col>
                                <Col className="col-12 col-md-4 mt-4">
                                    <SelectField
                                        label="Permissions"
                                        name="permissions"
                                        options={Object.values(PERMISSIONS).map((permission) => ({ label: permission, value: permission }))}
                                        value={state.permissions}
                                        onChange={(name, value) => handleForm({ [name]: value })}
                                        minHeight={55}
                                    />
                                </Col>
                            </Row>
                        </div>

                        <RelatedSeminarsSection
                            selectedSeminars={selectedSeminars}
                            seminarList={seminarList}
                            sessionsList={sessionsList}
                            state={state}
                            onSeminarChange={handleSeminar}
                            onSessionChange={handleSession}
                            onRemoveSeminar={handleRemoveSeminar}
                            onAddSeminar={handleAddSeminar}
                            getFilteredOptionsForSection={getFilteredOptionsForSection}
                        />

                        <RelatedTemplatesSection
                            selectedTemplates={selectedTemplates}
                            templateList={templateList}
                            state={state}
                            onTemplateChange={handleTemplate}
                            onTabChange={handleTemplateTab}
                            onRemoveTemplate={handleRemoveTemplateByIndex}
                            onAddTemplate={handleAddTemplate}
                            onSyncTemplateTabs={handleSyncTemplateTabs}
                            getFilteredOptionsForTemplate={getFilteredOptionsForTemplate}
                        />

                        <ContentItemsSection
                            contentItems={state.contentItems}
                            contentItemList={contentItemList}
                            onContentItemsChange={(updatedContentItems) => handleForm({ contentItems: updatedContentItems })}
                            onContentItemPreview={(content) => {
                                if (content.type === 'external_link') {
                                    window.open(content.link, '_blank');
                                }
                            }}
                        />
                    </Form>
                </div>
            </div>
        </div>
    )
}